import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { getPiiConsentController, getVisitorId } from '@mutiny-pkg/analytics-tracker';
import {
  COMPONENT_STATE_CAPABILITY_HEADER,
  COMPONENT_STATE_VISITOR_HEADER,
  ComponentStateErrorResponseSchema,
  ComponentStateErrorCodeSchema,
  ComponentStateEmailSchema,
  ComponentStateListResponseSchema,
  buildComponentStateIdentity,
  componentStateCollectionPath,
  componentStateDocumentPath,
  type ComponentStateData,
  type ComponentStateDocument,
  type ComponentStateErrorCode,
  type ComponentStateIdentity,
  type ComponentStateMutationRequest,
  type ComponentStateQuery,
  type ComponentStateValue,
} from './componentStateApiContract';

export type StateValue = ComponentStateValue;
export type StateData = ComponentStateData;

export interface ComponentStateError extends Error {
  code: ComponentStateErrorCode;
}

export type StateDocument = ComponentStateDocument;

export interface CollectionQuery {
  orderBy?: 'createdAt' | 'updatedAt';
  direction?: 'asc' | 'desc';
  limit?: number;
  cursor?: string;
  where?: { field: string; equals: StateValue };
}

export interface CollectionPage {
  docs: StateDocument[];
  nextCursor: string | null;
}

export interface CollectionHandle {
  docs: StateDocument[];
  documents: StateDocument[];
  status: 'loading' | 'ready' | 'error';
  loading: boolean;
  error?: ComponentStateError;
  nextCursor: string | null;
  insert(data: StateData, id?: string): Promise<string>;
  update(id: string, data: StateData): Promise<void>;
  mutate(id: string, mutation: ComponentStateMutationRequest): Promise<void>;
  remove(id: string): Promise<void>;
  refresh(): Promise<void>;
}

export interface ComponentStateClient {
  readonly viewer: ComponentStateIdentity;
  subscribeViewer(listener: () => void): () => void;
  list(collection: string, query?: ComponentStateQuery): Promise<CollectionPage>;
  insert(collection: string, id: string, data: StateData): Promise<void>;
  replace(collection: string, id: string, data: StateData): Promise<void>;
  mutate(collection: string, id: string, mutation: ComponentStateMutationRequest): Promise<void>;
  remove(collection: string, id: string): Promise<void>;
}

interface StateRuntime {
  useCollection(name: string, query?: CollectionQuery): CollectionHandle;
  useViewer(): ComponentStateClient['viewer'];
  isCurrentViewer(identity: ComponentStateIdentity): boolean;
  canEdit(document: StateDocument): boolean;
  getCollection(name: string): Pick<CollectionHandle, 'insert' | 'update' | 'mutate' | 'remove'> & {
    list(query?: CollectionQuery): Promise<CollectionPage>;
  };
}

function stateError(code: ComponentStateErrorCode): ComponentStateError {
  return Object.assign(new Error(code), { code });
}

async function parseError(response: Response): Promise<ComponentStateError> {
  const body = ComponentStateErrorResponseSchema.safeParse(await response.json().catch(() => null));
  return stateError(body.success ? body.data.error.code : 'server_error');
}

function readEmailGateIdentity(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|;\s*)_mutiny_email_gate_email=([^;]+)/u);
  if (!match?.[1]) return null;
  try {
    const email = ComponentStateEmailSchema.safeParse(decodeURIComponent(match[1]));
    return email.success ? email.data : null;
  } catch {
    return null;
  }
}

export const EMAIL_GATE_IDENTITY_CHANGED_EVENT = 'mutiny:email-gate-identity-changed';

export function createHttpComponentStateClient(input: {
  endpoint: string;
  capability: string;
  defaultOptOutPiiTracking: boolean;
}): ComponentStateClient {
  const consent = getPiiConsentController();
  consent.resolveEnabled(input.defaultOptOutPiiTracking);
  let resolvedVisitorId: string | null = null;
  const visitorId = () => {
    resolvedVisitorId ??= getVisitorId();
    return resolvedVisitorId;
  };
  const optedOutViewer: ComponentStateIdentity = { kind: 'anonymous', visitorId: '', email: null };
  let viewerSnapshot = consent.isPiiTrackingEnabled()
    ? buildComponentStateIdentity(visitorId(), readEmailGateIdentity())
    : optedOutViewer;
  const viewerListeners = new Set<() => void>();
  const refreshViewer = () => {
    const nextViewer = consent.isPiiTrackingEnabled()
      ? buildComponentStateIdentity(visitorId(), readEmailGateIdentity())
      : optedOutViewer;
    if (
      nextViewer.kind === viewerSnapshot.kind &&
      nextViewer.visitorId === viewerSnapshot.visitorId &&
      nextViewer.email === viewerSnapshot.email
    ) {
      return;
    }
    viewerSnapshot = nextViewer;
    for (const listener of viewerListeners) listener();
  };
  const request = async (path: string, init?: RequestInit): Promise<Response> => {
    refreshViewer();
    if (!consent.isPiiTrackingEnabled()) throw stateError('unauthorized');
    const response = await fetch(`${input.endpoint}${path}`, {
      ...init,
      headers: {
        ...init?.headers,
        [COMPONENT_STATE_CAPABILITY_HEADER]: input.capability,
        [COMPONENT_STATE_VISITOR_HEADER]: visitorId(),
      },
    });
    if (!response.ok) throw await parseError(response);
    return response;
  };

  return {
    get viewer() {
      return viewerSnapshot;
    },
    subscribeViewer(listener) {
      viewerListeners.add(listener);
      window.addEventListener('focus', refreshViewer);
      window.addEventListener('pageshow', refreshViewer);
      window.addEventListener(EMAIL_GATE_IDENTITY_CHANGED_EVENT, refreshViewer);
      const unsubscribeConsent = consent.subscribe(() => queueMicrotask(refreshViewer));
      return () => {
        viewerListeners.delete(listener);
        window.removeEventListener('focus', refreshViewer);
        window.removeEventListener('pageshow', refreshViewer);
        window.removeEventListener(EMAIL_GATE_IDENTITY_CHANGED_EVENT, refreshViewer);
        unsubscribeConsent();
      };
    },
    async list(collection, query) {
      const response = await request(componentStateCollectionPath(collection, query));
      const body = ComponentStateListResponseSchema.safeParse(await response.json());
      if (!body.success) throw stateError('unauthorized');
      return body.data;
    },
    async insert(collection, id, data) {
      await request(componentStateCollectionPath(collection), {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ id, data }),
      });
    },
    async replace(collection, id, data) {
      await request(componentStateDocumentPath(collection, id), {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ data }),
      });
    },
    async mutate(collection, id, mutation) {
      await request(componentStateDocumentPath(collection, id), {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(mutation),
      });
    },
    async remove(collection, id) {
      await request(componentStateDocumentPath(collection, id), {
        method: 'DELETE',
      });
    },
  };
}

export function createConfiguredComponentStateClient(input?: {
  endpoint: string;
  capability: string;
  defaultOptOutPiiTracking: boolean;
}): ComponentStateClient {
  return input && typeof window !== 'undefined' && typeof document !== 'undefined'
    ? createHttpComponentStateClient(input)
    : createMemoryComponentStateClient();
}

export function createMemoryComponentStateClient(): ComponentStateClient {
  const visitorId = crypto.randomUUID();
  const collections = new Map<string, StateDocument[]>();
  return {
    viewer: buildComponentStateIdentity(visitorId, null),
    subscribeViewer() {
      return () => undefined;
    },
    async list(collection) {
      return { docs: collections.get(collection) ?? [], nextCursor: null };
    },
    async insert(collection, id, data) {
      const docs = collections.get(collection) ?? [];
      if (docs.some((doc) => doc.id === id)) return;
      const now = new Date().toISOString();
      collections.set(collection, [
        ...docs,
        {
          id,
          data,
          writer: buildComponentStateIdentity(visitorId, null),
          createdAt: now,
          updatedAt: now,
          version: crypto.randomUUID(),
        },
      ]);
    },
    async replace(collection, id, data) {
      const docs = collections.get(collection) ?? [];
      if (!docs.some((doc) => doc.id === id)) throw stateError('not_found');
      collections.set(
        collection,
        docs.map((doc) =>
          doc.id === id ? { ...doc, data, updatedAt: new Date().toISOString(), version: crypto.randomUUID() } : doc,
        ),
      );
    },
    async mutate(collection, id, mutation) {
      const docs = collections.get(collection) ?? [];
      const existing = docs.find((doc) => doc.id === id);
      if (!existing) throw stateError('not_found');
      if (mutation.expectedVersion && mutation.expectedVersion !== existing.version) throw stateError('conflict');
      const data = applyMemoryMutation(existing.data, mutation);
      collections.set(
        collection,
        docs.map((doc) =>
          doc.id === id ? { ...doc, data, updatedAt: new Date().toISOString(), version: crypto.randomUUID() } : doc,
        ),
      );
    },
    async remove(collection, id) {
      const docs = collections.get(collection) ?? [];
      if (!docs.some((doc) => doc.id === id)) throw stateError('not_found');
      collections.set(
        collection,
        docs.filter((doc) => doc.id !== id),
      );
    },
  };
}

export function createComponentStateRuntime(client: ComponentStateClient): StateRuntime {
  function getCollection(name: string) {
    const toTransportQuery = (query?: CollectionQuery): ComponentStateQuery | undefined =>
      query
        ? {
            ...(query.orderBy ? { orderBy: query.orderBy } : {}),
            ...(query.direction ? { direction: query.direction } : {}),
            ...(query.limit !== undefined ? { limit: query.limit } : {}),
            ...(query.cursor ? { cursor: query.cursor } : {}),
            ...(query.where ? { whereField: query.where.field, whereEquals: JSON.stringify(query.where.equals) } : {}),
          }
        : undefined;
    return {
      list: (query?: CollectionQuery) => client.list(name, toTransportQuery(query)),
      async insert(data: StateData, id: string = crypto.randomUUID()) {
        await client.insert(name, id, data);
        return id;
      },
      async update(id: string, data: StateData) {
        await client.replace(name, id, data);
      },
      async mutate(id: string, mutation: ComponentStateMutationRequest) {
        await client.mutate(name, id, mutation);
      },
      async remove(id: string) {
        await client.remove(name, id);
      },
    };
  }

  function useCollection(name: string, query?: CollectionQuery): CollectionHandle {
    const collection = useMemo(() => getCollection(name), [name]);
    const [docs, setDocs] = useState<StateDocument[]>([]);
    const [status, setStatus] = useState<CollectionHandle['status']>('loading');
    const [error, setError] = useState<ComponentStateError>();
    const [nextCursor, setNextCursor] = useState<string | null>(null);
    const serializedQuery = JSON.stringify(query ?? {});
    const querySnapshot = useRef({ serialized: serializedQuery, value: query });
    if (querySnapshot.current.serialized !== serializedQuery) {
      querySnapshot.current = { serialized: serializedQuery, value: query };
    }
    const refreshSequence = useRef(0);
    const refresh = useCallback(async () => {
      const sequence = ++refreshSequence.current;
      try {
        const next = await collection.list(querySnapshot.current.value);
        if (sequence !== refreshSequence.current) return;
        setDocs(next.docs);
        setNextCursor(next.nextCursor);
        setStatus('ready');
        setError(undefined);
      } catch (cause) {
        if (sequence !== refreshSequence.current) return;
        setStatus('error');
        setError(isComponentStateError(cause) ? cause : stateError('server_error'));
      }
    }, [collection]);
    useEffect(() => {
      void refresh();
      return () => {
        refreshSequence.current += 1;
      };
    }, [refresh, serializedQuery]);
    return {
      docs,
      documents: docs,
      status,
      loading: status === 'loading',
      error,
      nextCursor,
      async insert(data, id) {
        const insertedId = await collection.insert(data, id);
        await refresh();
        return insertedId;
      },
      async update(id, data) {
        const existing = docs.find((document) => document.id === id);
        if (!existing) throw stateError('not_found');
        await collection.update(id, { ...existing.data, ...data });
        await refresh();
      },
      async mutate(id, mutation) {
        await collection.mutate(id, mutation);
        await refresh();
      },
      async remove(id) {
        await collection.remove(id);
        await refresh();
      },
      refresh,
    };
  }

  const serverViewer: ComponentStateIdentity = { kind: 'anonymous', visitorId: '', email: null };
  function useViewer() {
    return useSyncExternalStore(
      client.subscribeViewer,
      () => client.viewer,
      () => serverViewer,
    );
  }

  function isCurrentViewer(identity: ComponentStateIdentity): boolean {
    return identity.visitorId === client.viewer.visitorId;
  }

  function canEdit(document: StateDocument): boolean {
    return isCurrentViewer(document.writer);
  }

  return { useCollection, useViewer, isCurrentViewer, canEdit, getCollection };
}

export type ComponentStateRuntime = ReturnType<typeof createComponentStateRuntime>;

function isComponentStateError(error: unknown): error is ComponentStateError {
  if (!(error instanceof Error) || !('code' in error)) return false;
  return ComponentStateErrorCodeSchema.safeParse(error.code).success;
}

function applyMemoryMutation(data: StateData, mutation: ComponentStateMutationRequest): StateData {
  if (mutation.operation === 'merge') return { ...data, ...mutation.data };
  const current = data[mutation.field];
  if (mutation.operation === 'increment') {
    if (current !== undefined && typeof current !== 'number') throw stateError('invalid_operation');
    return { ...data, [mutation.field]: (current ?? 0) + mutation.amount };
  }
  if (mutation.operation === 'toggle') {
    if (current !== undefined && typeof current !== 'boolean') throw stateError('invalid_operation');
    return { ...data, [mutation.field]: !(current ?? false) };
  }
  if (current !== undefined && !Array.isArray(current)) throw stateError('invalid_operation');
  return { ...data, [mutation.field]: [...(current ?? []), mutation.value] };
}
